"use client";

import { useState } from "react";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { t, tPlural } from "@/lib/i18n/interpolate";

interface CompareOptions {
  caseInsensitive: boolean;
  trim: boolean;
  ignoreDuplicates: boolean;
}

interface CompareResult {
  inBoth: string[];
  onlyA: string[];
  onlyB: string[];
  totalA: number;
  totalB: number;
}

function normalizeKey(item: string, caseInsensitive: boolean): string {
  return caseInsensitive ? item.toLowerCase() : item;
}

function parseList(text: string, options: CompareOptions): string[] {
  let items = text.split("\n");
  if (options.trim) {
    items = items.map((item) => item.trim());
  }
  items = items.filter((item) => item.length > 0);
  if (!options.ignoreDuplicates) {
    return items;
  }
  const seen = new Set<string>();
  const unique: string[] = [];
  for (const item of items) {
    const key = normalizeKey(item, options.caseInsensitive);
    if (!seen.has(key)) {
      seen.add(key);
      unique.push(item);
    }
  }
  return unique;
}

function compareLists(
  textA: string,
  textB: string,
  options: CompareOptions
): CompareResult {
  const itemsA = parseList(textA, options);
  const itemsB = parseList(textB, options);
  const keysA = new Set(
    itemsA.map((item) => normalizeKey(item, options.caseInsensitive))
  );
  const keysB = new Set(
    itemsB.map((item) => normalizeKey(item, options.caseInsensitive))
  );
  return {
    inBoth: itemsA.filter((item) =>
      keysB.has(normalizeKey(item, options.caseInsensitive))
    ),
    onlyA: itemsA.filter(
      (item) => !keysB.has(normalizeKey(item, options.caseInsensitive))
    ),
    onlyB: itemsB.filter(
      (item) => !keysA.has(normalizeKey(item, options.caseInsensitive))
    ),
    totalA: itemsA.length,
    totalB: itemsB.length,
  };
}

/*
 * The summary was a template literal in English with two suffix ternaries in it, and it
 * is the string this tool exists to hand the reader — so on a translated page the whole
 * result read English. Two plural nouns fill a frame the language can reorder.
 */
function buildSummary(
  result: CompareResult,
  strings: typeof COMPARE_LISTS_STRINGS,
  locale: string
): string {
  return t(strings.summary, {
    listA: tPlural(strings.itemCount, locale, result.totalA).trim(),
    listB: tPlural(strings.itemCount, locale, result.totalB).trim(),
    shared: result.inBoth.length,
    onlyA: result.onlyA.length,
    onlyB: result.onlyB.length,
  });
}

function ResultCard({
  title,
  items,
  copied,
  onCopy,
}: {
  title: string;
  items: string[];
  copied: boolean;
  onCopy: () => void;
}) {
  const fmt = useToolNumber();
  const strings = useToolUi(COMPARE_LISTS_STRINGS);
  return (
    <div className="flex flex-col rounded-lg border border-border bg-card p-4">
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="font-medium text-foreground text-sm">
          {title}{" "}
          <span className="text-muted-foreground">({fmt(items.length)})</span>
        </p>
        <button
          className="rounded-lg border border-border bg-background px-3 py-1 font-medium text-foreground text-xs transition-colors hover:bg-muted disabled:opacity-40"
          disabled={items.length === 0}
          onClick={onCopy}
          type="button"
        >
          {copied ? strings.copied : strings.copy}
        </button>
      </div>
      <textarea
        className="min-h-[160px] w-full flex-1 resize-y rounded-lg border border-border bg-background p-3 font-mono text-foreground text-xs leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary"
        readOnly
        value={items.join("\n")}
      />
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const COMPARE_LISTS_STRINGS = {
  summary:
    "List A has {listA} and List B has {listB}. They share {shared} in the overlap, {onlyA} appear only in A, and {onlyB} appear only in B.",
  itemCount: "one: {count} item|other: {count} items",
  listAOneItemPer: "List A — one item per line",
  appleBananaCherry: "Apple\nBanana\nCherry",
  listBOneItemPer: "List B — one item per line",
  bananaCherryDate: "Banana\nCherry\nDate",
  caseInsensitive: "Case-insensitive",
  trimWhitespace: "Trim whitespace",
  ignoreDuplicates: "Ignore duplicates",
  compare: "Compare",
  clear: "Clear",
  inBoth: "In both",
  onlyInA: "Only in A",
  onlyInB: "Only in B",
  copy: "Copy",
  copied: "Copied!",
} as const;

export default function CompareListsWidget() {
  const [listA, setListA] = useState("");
  const [listB, setListB] = useState("");
  const [caseInsensitive, setCaseInsensitive] = useState(true);
  const [trim, setTrim] = useState(true);
  const [ignoreDuplicates, setIgnoreDuplicates] = useState(true);
  const [result, setResult] = useState<CompareResult | null>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  function handleCompare() {
    if (listA.trim().length === 0 && listB.trim().length === 0) {
      return;
    }
    setResult(
      compareLists(listA, listB, { caseInsensitive, trim, ignoreDuplicates })
    );
    setCopiedKey(null);
  }

  function handleCopy(key: string, items: string[]) {
    writeToClipboard(items.join("\n")).then((copied) => {
      if (!copied) {
        return;
      }

      setCopiedKey(key);
      setTimeout(
        () => setCopiedKey((current) => (current === key ? null : current)),
        2000
      );
    });
  }

  function handleClear() {
    setListA("");
    setListB("");
    setResult(null);
    setCopiedKey(null);
  }

  const strings = useToolUi(COMPARE_LISTS_STRINGS);
  const locale = useToolLocale();

  return (
    <>
      {/* Inputs */}
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="list-a"
          >
            {strings.listAOneItemPer}
          </label>
          <textarea
            className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            id="list-a"
            onChange={(e) => setListA(e.target.value)}
            placeholder={strings.appleBananaCherry}
            value={listA}
          />
        </div>
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="list-b"
          >
            {strings.listBOneItemPer}
          </label>
          <textarea
            className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            id="list-b"
            onChange={(e) => setListB(e.target.value)}
            placeholder={strings.bananaCherryDate}
            value={listB}
          />
        </div>
      </div>
      {/* Options */}
      <section className="flex flex-wrap gap-3">
        <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm">
          <input
            checked={caseInsensitive}
            className="accent-primary"
            onChange={(e) => setCaseInsensitive(e.target.checked)}
            type="checkbox"
          />
          {strings.caseInsensitive}
        </label>
        <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm">
          <input
            checked={trim}
            className="accent-primary"
            onChange={(e) => setTrim(e.target.checked)}
            type="checkbox"
          />
          {strings.trimWhitespace}
        </label>
        <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm">
          <input
            checked={ignoreDuplicates}
            className="accent-primary"
            onChange={(e) => setIgnoreDuplicates(e.target.checked)}
            type="checkbox"
          />
          {strings.ignoreDuplicates}
        </label>
      </section>
      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={listA.trim().length === 0 && listB.trim().length === 0}
          onClick={handleCompare}
          type="button"
        >
          {strings.compare}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={listA.length === 0 && listB.length === 0 && !result}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      {/* Results */}
      {result && (
        <>
          <p className="rounded-lg border border-border bg-muted/30 p-4 text-foreground text-sm leading-relaxed">
            {buildSummary(result, strings, locale)}
          </p>
          <div className="grid gap-4 md:grid-cols-3">
            <ResultCard
              copied={copiedKey === "both"}
              items={result.inBoth}
              onCopy={() => handleCopy("both", result.inBoth)}
              title={strings.inBoth}
            />
            <ResultCard
              copied={copiedKey === "a"}
              items={result.onlyA}
              onCopy={() => handleCopy("a", result.onlyA)}
              title={strings.onlyInA}
            />
            <ResultCard
              copied={copiedKey === "b"}
              items={result.onlyB}
              onCopy={() => handleCopy("b", result.onlyB)}
              title={strings.onlyInB}
            />
          </div>
        </>
      )}
    </>
  );
}
